import React, { createContext, useContext, useState, useCallback } from 'react';

const MenuBuilderContext = createContext(null);

export const useMenuBuilder = () => {
  const context = useContext(MenuBuilderContext);
  if (!context) {
    throw new Error('useMenuBuilder must be used within MenuBuilderProvider');
  }
  return context;
};

export const MenuBuilderProvider = ({ children }) => {
  const [menuData, setMenuData] = useState({
    name: '',
    chatBarText: 'Menu',
    size: { width: 2500, height: 1686 },
    areas: [],
    selected: false,
    imageUrl: null,
    richMenuAliasId: '',
    pendingImageFile: null
  });

  const [selectedAreaIndex, setSelectedAreaIndex] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState(null);

  const updateMenuData = useCallback((updates) => {
    setMenuData(prev => ({ ...prev, ...updates }));
  }, []);

  const addArea = useCallback((area) => {
    setMenuData(prev => ({
      ...prev,
      areas: [...prev.areas, area]
    }));
    setSelectedAreaIndex(menuData.areas.length);
  }, [menuData.areas.length]);

  const updateArea = useCallback((index, updates) => {
    setMenuData(prev => ({
      ...prev,
      areas: prev.areas.map((area, i) =>
        i === index ? { ...area, ...updates } : area
      )
    }));
  }, []);

  const removeArea = useCallback((index) => {
    setMenuData(prev => ({
      ...prev,
      areas: prev.areas.filter((_, i) => i !== index)
    }));
    setSelectedAreaIndex(null);
  }, []);

  const selectArea = useCallback((index) => {
    setSelectedAreaIndex(index);
  }, []);

  const clearAreas = useCallback(() => {
    setMenuData(prev => ({ ...prev, areas: [] }));
    setSelectedAreaIndex(null);
  }, []);

  const loadTemplate = useCallback((template) => {
    setMenuData(prev => ({
      ...prev,
      size: template.size,
      areas: template.areas
    }));
    setSelectedAreaIndex(null);
  }, []);

  // loadMenu and resetMenu return the data they set, so the editor can record it as the saved state
  const loadMenu = useCallback((menu) => {
    const data = {
      name: menu.name || '',
      chatBarText: menu.chatBarText || 'Menu',
      size: menu.size || { width: 2500, height: 1686 },
      areas: menu.areas || [],
      selected: menu.selected || false,
      imageUrl: menu.imageUrl || null,
      richMenuAliasId: menu.richMenuAliasId || '',
      pendingImageFile: null
    };
    setMenuData(data);
    setSelectedAreaIndex(null);
    return data;
  }, []);

  const resetMenu = useCallback(() => {
    const data = {
      name: '',
      chatBarText: 'Menu',
      size: { width: 2500, height: 1686 },
      areas: [],
      selected: false,
      imageUrl: null,
      richMenuAliasId: '',
      pendingImageFile: null
    };
    setMenuData(data);
    setSelectedAreaIndex(null);
    return data;
  }, []);

  const value = {
    menuData,
    selectedAreaIndex,
    isDrawing,
    drawStart,
    setIsDrawing,
    setDrawStart,
    updateMenuData,
    addArea,
    updateArea,
    removeArea,
    selectArea,
    clearAreas,
    loadTemplate,
    loadMenu,
    resetMenu
  };

  return (
    <MenuBuilderContext.Provider value={value}>
      {children}
    </MenuBuilderContext.Provider>
  );
};

export default MenuBuilderContext;
